import { useEffect, useRef, useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { formatMoney, parseMoneyInput } from '../../lib/formatters.ts'
import MoneyInput from '../../components/MoneyInput'
import { dollarsToWords } from '../../lib/dollarsToWords.ts'
import type { AssumptionRow as Row, ForecastEffect, ProjectTableRow } from '../../api/cashReport'
import { assumptionName, GENERIC_KIND_LABELS, KIND_LABELS, scopeLabel } from './assumptionLabels.ts'
import { confirmDeleteReduce, needsForecastEffect, type ConfirmDeleteState } from './confirmDelete.ts'
import { localDateKey } from '../../lib/localDate.ts'

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

type Props = {
  row: Row | null
  projects: ProjectTableRow[]
  adding?: boolean
  onCancelAdd?: () => void
  onEditBank?: (row: Row) => void
  onSave: (input: {
    id?: string
    kind: string
    amount_cents: number
    effective_date: string
    project_id: number | null
    reason?: string | null
  }) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onPreviewDelete: (id: string) => Promise<ForecastEffect>
}

function saveLabel(s: SaveState): string {
  if (s === 'saving') return 'Saving…'
  if (s === 'saved') return 'Saved ✓'
  if (s === 'error') return 'Save failed'
  return 'Save'
}

export default function AssumptionRow({
  row, projects, adding, onCancelAdd, onEditBank, onSave, onDelete, onPreviewDelete,
}: Props) {
  const [mode, setMode] = useState<'view' | 'edit'>(adding ? 'edit' : 'view')
  const [del, setDel] = useState<ConfirmDeleteState>('idle')
  const [effect, setEffect] = useState<ForecastEffect | null>(null)
  const [effectStatus, setEffectStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
  const [save, setSave] = useState<SaveState>('idle')
  const [kind, setKind] = useState(row?.kind || 'one_time_expense')
  const [amount, setAmount] = useState(row?.amount_cents != null ? (Number(row.amount_cents) / 100).toFixed(2) : '')
  const [date, setDate] = useState((row?.effective_date || localDateKey()).slice(0, 10))
  const [projectId, setProjectId] = useState(row?.project_id != null ? String(row.project_id) : '')
  const [reason, setReason] = useState(row?.reason || '')
  const saveGen = useRef(0)

  useEffect(() => { if (adding) setMode('edit') }, [adding])
  useEffect(() => {
    if (mode === 'view' && row?.amount_cents != null) setAmount((Number(row.amount_cents) / 100).toFixed(2))
  }, [row?.amount_cents, row?.id, mode])

  const dollars = parseMoneyInput(amount)
  const canSave = Number.isFinite(dollars) && !!date
    && (kind !== 'receipt_date_override' || !!reason.trim())

  function openEdit() {
    if (row?.kind === 'cash_on_hand' && onEditBank) {
      onEditBank(row)
      return
    }
    setMode('edit')
  }

  async function submit() {
    if (!canSave) return
    const gen = ++saveGen.current
    setSave('saving')
    try {
      await onSave({
        id: row?.id,
        kind,
        amount_cents: Math.round(dollars * 100),
        effective_date: date,
        project_id: projectId ? Number(projectId) : null,
        reason: reason.trim() || null,
      })
      if (saveGen.current !== gen) return
      setSave('saved')
      setMode('view')
    } catch {
      if (saveGen.current !== gen) return
      setSave('error')
    }
  }

  async function askDelete() {
    setDel(confirmDeleteReduce(del, { type: 'ask' }))
    if (row && needsForecastEffect(row.amount_cents)) {
      setEffect(null)
      setEffectStatus('loading')
      let lastErr: unknown = null
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          setEffect(await onPreviewDelete(row.id))
          setEffectStatus('ready')
          return
        } catch (err) {
          lastErr = err
        }
      }
      void lastErr
      setEffectStatus('error')
    } else {
      setEffectStatus('idle')
    }
  }

  async function confirmDelete() {
    if (!row) return
    setDel(confirmDeleteReduce(del, { type: 'confirm' }))
    try {
      await onDelete(row.id)
      setDel(confirmDeleteReduce('deleting', { type: 'done' }))
    } catch {
      setDel(confirmDeleteReduce('deleting', { type: 'fail' }))
    }
  }

  if (mode === 'edit') {
    return (
      <div className="cr-source-row editing" data-editable data-testid={row?.id ? `cr-assumption-${row.id}` : 'cr-assumption-form'}>
        <div className="cr-edit-fields">
          <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="cr-assump-kind">
            {Object.entries(GENERIC_KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <select aria-label="Scope" value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="cr-assump-scope">
            <option value="">Company</option>
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
          </select>
          <MoneyInput aria-label="Amount" value={amount} onChange={setAmount} data-testid="cr-assump-amount" />
          <input aria-label="Due date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="cr-assump-date" />
          {kind === 'receipt_date_override' ? (
            <input aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" data-testid="cr-assump-reason" />
          ) : null}
          {Number.isFinite(dollars) ? (
            <span className="cr-edit-readback" data-testid="cr-words-readback">{dollarsToWords(dollars)}</span>
          ) : null}
          <span className="cr-edit-actions">
            <button type="button" className="cr-btn dark" disabled={!canSave || save === 'saving'} onClick={submit} data-testid="cr-assumption-save">{saveLabel(save)}</button>
            <button type="button" className="cr-btn" onClick={() => { setMode('view'); onCancelAdd?.() }} data-testid="cr-assumption-cancel">Cancel</button>
          </span>
        </div>
      </div>
    )
  }

  if (!row) return null
  const project = projects.find((p) => p.project_id === row.project_id)
  const confirming = del === 'confirming' || del === 'deleting'
  return (
    <div
      className={`cr-source-row${confirming ? ' confirming' : ''}`}
      data-editable
      data-kind={row.kind}
      data-testid={`cr-assumption-${row.id}`}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' && !confirming) openEdit() }}
    >
      <div className="cr-source-main cr-source-display">
        <button type="button" onClick={openEdit}>
          {assumptionName(row)} <span className="cr-editable-tag">Editable</span>
        </button>
        <span>
          {scopeLabel(row.project_id, project?.project_name)} · {KIND_LABELS[row.kind] || row.kind}
          {row.effective_date ? ` · due ${String(row.effective_date).slice(0, 10)}` : ''}
        </span>
      </div>
      <span className="cr-source-amount cr-source-display">{formatMoney((Number(row.amount_cents) || 0) / 100)}</span>
      <div className="cr-row-actions cr-source-display">
        <div className="cr-standard-actions">
          <button type="button" aria-label="Edit" data-testid="cr-assumption-edit" onClick={openEdit}><Pencil size={14} /></button>
          <button type="button" aria-label="Delete" data-testid="cr-assumption-delete" onClick={askDelete}><Trash2 size={14} /></button>
        </div>
        <div className="cr-delete-confirm">
          {effectStatus === 'loading' || effectStatus === 'ready' || effectStatus === 'error' ? (
            <p className="cr-forecast-effect" data-testid="cr-forecast-effect">
              {effectStatus === 'loading' && !effect
                ? 'This week — checking forecast…'
                : effectStatus === 'error' && !effect
                  ? 'This week — preview unavailable'
                  : (
                    <>
                      This week {formatMoney((effect?.this_week.before_cents || 0) / 100)} → {formatMoney((effect?.this_week.after_cents || 0) / 100)}
                      {' · '}
                      Lowest point {formatMoney((effect?.lowest.before_cents || 0) / 100)} → {formatMoney((effect?.lowest.after_cents || 0) / 100)}
                    </>
                  )}
            </p>
          ) : null}
          <button type="button" className="cr-btn danger" data-testid="cr-assumption-confirm-delete" onClick={confirmDelete}>Confirm delete</button>
          <button type="button" className="cr-btn" data-testid="cr-assumption-cancel-delete" onClick={() => { setDel('idle'); setEffect(null); setEffectStatus('idle') }}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
